/**
 * Connectors — one tabbed manager: Entities · Channels · Tools · File buckets.
 * Connected instances are grouped by their server-declared `type`.
 *
 * NOTE (data-plane): the connector instance list is live via the outlet (/auth/*).
 * Entity and file detail data must also stay on the outlet/channel path;
 * any missing detail is surfaced as pending instead of routing to a legacy service.
 */
import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { Database, MessageSquare, Wrench, FolderOpen, Plus, Lock, Trash2, X } from "lucide-react";

import { FileBuckets } from "./FileBuckets";
import { IntegrationManager } from "./IntegrationManager";
import { integrationService } from "../services/IntegrationService";
import type { IntegrationServerInstance, ScopeCategory } from "@/schema";

type Tab = ScopeCategory;

interface SourcesSectionProps {
  mode?: "categorized" | "all";
  showHeader?: boolean;
  embedded?: boolean;
  addRequestKey?: number;
}

const TABS: { id: Tab; label: string; Icon: typeof Database }[] = [
  { id: "source", label: "Entities", Icon: Database },
  { id: "communication", label: "Channels", Icon: MessageSquare },
  { id: "action", label: "Tools", Icon: Wrench },
  { id: "file", label: "File buckets", Icon: FolderOpen },
];

// causit owns a real instance (returned by getInstances) — read-only, in
// Entities, never offered in the add modal. Detected by serverName.
const isCausit = (i: IntegrationServerInstance) => i.serverName === "causit";
const categoriesOf = (i: IntegrationServerInstance): ScopeCategory[] =>
  i.type ? [i.type] : [];

const STATUS_TONE: Record<string, string> = {
  connected: "#22c55e", connecting: "#f59e0b", selectionPending: "#f97316",
  error: "#ef4444", disconnected: "#9ca3af",
};

export const SourcesSection = ({
  mode = "categorized",
  showHeader = true,
  embedded = false,
  addRequestKey = 0,
}: SourcesSectionProps) => {
  const [tab, setTab] = useState<Tab>("source");
  const [instances, setInstances] = useState<IntegrationServerInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState<IntegrationServerInstance | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // actor=integration returns authenticated instances; tabs narrow by the
      // instance type from the outlet contract.
      const { items } = await integrationService.list({ actor: "integration" });
      setInstances((items as IntegrationServerInstance[]) || []);
    } catch {
      setInstances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshKey]);
  useEffect(() => {
    if (addRequestKey > 0) setShowAdd(true);
  }, [addRequestKey]);

  const forTab = (t: Exclude<Tab, "file">) =>
    instances.filter((i) => categoriesOf(i).includes(t));

  return (
    <div style={embedded ? undefined : sectionCard}>
      {showHeader && (
        /* Header row: label | tabs centered | + */
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-secondary)" }}>
            Connectors
          </span>
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                title={label}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 28, height: 28, borderRadius: 6, border: "none",
                  background: tab === id ? "var(--color-bg)" : "transparent",
                  color: tab === id ? "var(--color-text)" : "var(--color-text-secondary)",
                  cursor: "pointer",
                }}
              >
                <Icon size={13} />
              </button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowAdd(true)}
              title="Add connector"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: 6, border: "none",
                background: "transparent", color: "var(--color-text-secondary)",
                cursor: "pointer",
              }}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      )}

      <div style={{ height: 214, overflowY: "auto", overflowX: "hidden" }}>
        {mode === "all" ? (
          <IntegrationTab
            instances={instances}
            loading={loading}
            onOpen={setDetail}
          />
        ) : tab === "file" ? (
          <FileBuckets isOpen surface="section" showHeader={false} readOnly refreshKey={refreshKey} />
        ) : (
          <IntegrationTab
            instances={forTab(tab)}
            loading={loading}
            onOpen={setDetail}
          />
        )}
      </div>

      {/* add modal — per connector category (causit + file excluded; they're managed) */}
      <IntegrationManager
        isOpen={showAdd}
        variant="addModal"
        onClose={() => setShowAdd(false)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      {detail && (
        <IntegrationDetail
          instance={detail}
          onClose={() => setDetail(null)}
          onReconnect={() => { setDetail(null); setShowAdd(true); }}
          onDisconnect={async () => {
            await integrationService.disconnectInstance(detail.instanceId, detail.auth?.type === "custom").catch(() => {});
            setDetail(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};

// ── tab body: a grid of connector boxes ──────────────────────────────────────
const IntegrationTab = ({
  instances, loading, onOpen,
}: {
  instances: IntegrationServerInstance[];
  loading: boolean;
  onOpen: (i: IntegrationServerInstance) => void;
}) => {
  if (loading) return <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: 8 }}>Loading…</div>;
  if (!instances.length) {
    return (
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: 8 }}>
        No integrations connected.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "start" }}>
      {instances.map((i) => (
        <button
          key={i.instanceId}
          onClick={() => onOpen(i)}
          style={{
            textAlign: "left", padding: "6px 8px 5px", borderRadius: 7,
            border: "1px solid var(--color-border)", background: "var(--color-surface)",
            cursor: "pointer", width: 140, minWidth: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i.displayInfo?.title || i.serverName}
            </span>
            {isCausit(i)
              ? <Lock size={10} style={{ flexShrink: 0, color: "var(--color-text-secondary)" }} />
              : <span style={{ width: 6, height: 6, borderRadius: 99, background: STATUS_TONE[i.status] || "#9ca3af", flexShrink: 0 }} />}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {i.displayInfo?.subtitle || i.instanceName}
          </div>
          <div style={{ display: "flex", gap: 3, marginTop: 4, flexWrap: "wrap" }}>
            {categoriesOf(i).map((c) => (
              <span key={c} style={{ fontSize: 10, padding: "0 4px", borderRadius: 3, background: "var(--color-bg)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", lineHeight: "16px" }}>{c}</span>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
};

// ── per-box detail drill ──────────────────────────────────────────────────
const IntegrationDetail = ({
  instance, onClose, onReconnect, onDisconnect,
}: {
  instance: IntegrationServerInstance;
  onClose: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
}) => {
  const readonly = isCausit(instance);
  const entities = instance.access?.entities;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} className="app-card" style={{ width: 480, maxWidth: "92vw", maxHeight: "82vh", overflowY: "auto", padding: 22 }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>{instance.displayInfo?.title || instance.serverName}</div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{instance.displayInfo?.subtitle}</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-secondary)" }}><X className="w-4 h-4" /></button>
        </div>

        <Field label="status">{readonly ? "managed (read-only)" : instance.status}</Field>
        <Field label="categories">{categoriesOf(instance).join(", ")}</Field>
        {instance.access && (
          <Field label="access">
            tools: {instance.access.tools.read ? "read" : ""}{instance.access.tools.write ? " write" : ""} · entities: {entities?.mode}
          </Field>
        )}

        {/* entities of source / file tree */}
        <div style={{ marginTop: 12 }}>
          <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>entities</div>
          {entities?.mode === "allow" && entities.allow.length > 0 ? (
            <ul style={{ fontSize: 12, color: "var(--text-secondary)", listStyle: "none", padding: 0 }}>
              {entities.allow.map((e) => <li key={e}>• {e}</li>)}
            </ul>
          ) : (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Live entities/file-tree load via the outlet/channel path is pending for this connector.
            </div>
          )}
        </div>

        {!readonly && (
          <div className="flex gap-2 mt-5 justify-end">
            <button onClick={onReconnect} className="px-3 py-1.5 rounded-lg text-sm" style={{ background: "var(--bg-tertiary)", color: "var(--text-primary)" }}>Update</button>
            <button onClick={onDisconnect} className="px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1" style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
              <Trash2 className="w-3.5 h-3.5" /> Disconnect
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const sectionCard: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  padding: "12px 12px 14px",
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)" }}>{label}</div>
    <div style={{ color: "var(--color-text)", fontSize: 13 }}>{children}</div>
  </div>
);
